import type { PropsWithChildren, ReactNode } from 'react';
import { formatNumber, formatPercent, formatUsd, stateLabel } from './format';
import type { EvidenceKind, EvidenceState, EvidenceValue, SourceRecord } from './types';

export function SourceBadge({ kind, children }: PropsWithChildren<{ kind: EvidenceKind }>) {
  return <span className={`source-badge source-${kind}`}>{children}</span>;
}

export function StatePill({ state }: { state: EvidenceState }) {
  return <span className={`state-pill state-${state}`}>{stateLabel(state)}</span>;
}

type MetricFormat = 'usd' | 'number' | 'percent' | 'rank';
export function MetricCell({ label, metric, format = 'number', digits = 0, note }: { label: string; metric: EvidenceValue; format?: MetricFormat; digits?: number; note?: string }) {
  const value = metric.value as number | null;
  const display = format === 'usd' ? formatUsd(value) : format === 'percent' ? formatPercent(value, digits) : format === 'rank' ? value === null ? 'N/A' : `#${formatNumber(value)}` : formatNumber(value, digits);
  return <article className={`metric-cell is-${metric.state}`}>
    <div className="metric-label"><span>{label}</span><StatePill state={metric.state} /></div>
    <strong>{display}</strong>
    <small>{note || metric.limitations[0] || metric.sourceId}</small>
    <div className="metric-provenance">{metric.sourceId}<span>{metric.indexedBlock ? `BLK ${formatNumber(metric.indexedBlock)}` : metric.window?.cadence?.toUpperCase() || ''}</span></div>
  </article>;
}

export function SectionHead({ number, eyebrow, title, accent, description, controls }: { number: string; eyebrow: string; title: string; accent?: string; description: string; controls?: ReactNode }) {
  return <header className="section-head">
    <div>
      <p>{number} / {eyebrow}</p>
      <h2>{title}{accent ? <> <em>{accent}</em></> : null}</h2>
    </div>
    <div className="section-context"><p>{description}</p>{controls}</div>
  </header>;
}

export function MethodNote({ title = 'METHOD', children }: PropsWithChildren<{ title?: string }>) {
  return <aside className="method-note"><b>{title}</b><p>{children}</p></aside>;
}

export function EmptyState({ state = 'unavailable', title, children }: PropsWithChildren<{ state?: EvidenceState; title: string }>) {
  return <div className={`empty-state state-box-${state}`} role="status"><StatePill state={state} /><strong>{title}</strong><p>{children}</p></div>;
}

export function SourceLink({ source }: { source: SourceRecord }) {
  return <a className={`source-link source-status-${source.status}`} href={source.url} target="_blank" rel="noreferrer">
    <span>{source.status}</span><b>{source.label}</b><small>{source.note}</small>
  </a>;
}

export function Segmented<T extends string>({ label, value, options, onChange }: { label: string; value: T; options: Array<{ value: T; label: string }>; onChange: (value: T) => void }) {
  return <div className="segmented" role="group" aria-label={label}>{options.map((option) => <button key={option.value} type="button" aria-pressed={value === option.value} className={value === option.value ? 'is-active' : ''} onClick={() => onChange(option.value)}>{option.label}</button>)}</div>;
}

export function ChartCard({ eyebrow, title, badge, children, summary, className = '' }: PropsWithChildren<{ eyebrow: string; title: string; badge?: ReactNode; summary: string; className?: string }>) {
  return <figure className={`chart-card ${className}`} aria-label={summary}>
    <header><div><p>{eyebrow}</p><h3>{title}</h3></div>{badge}</header>
    {children}
    <figcaption className="sr-only">{summary}</figcaption>
  </figure>;
}

